import type { GetStaticPropsContext } from 'next';
import { pick } from 'lodash-es';
import { z } from 'zod';

import { PageCategory } from '~/features/analytics/constants';
import { getCatalogItemsFromServer } from '~/features/catalog/controllers/get-catalog-items-from-server';
import { CatalogItem } from '~/features/catalog/models/catalog-item';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { CourseCollectionSection } from '~/features/seo/components/course-collection-section';
import { FooterBannerSection } from '~/features/seo/components/footer-banner-section';
import { ProgramListSection } from '~/features/seo/components/program-list-section';
import { WaveHeroSection } from '~/features/seo/components/wave-hero-section';
import { getAllSubcategoryContents } from '~/features/seo/controllers/get-all-subcategory-contents';
import { getSubcategoryContent } from '~/features/seo/controllers/get-subcategory-content';
import { Subcategory } from '~/features/seo/models/subcategory';

export default function CourseCollectionPage({
  subcategory,
  page,
  programs,
}: {
  subcategory: Subcategory;
  page: Page;
  programs: Array<Pick<CatalogItem, 'key' | 'image' | 'summary' | 'slug' | 'programDescriptionOverride' | 'title'>>;
}) {
  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.Subcategory} />
      <PageLayout withoutVerticalPadding>
        <WaveHeroSection description={subcategory.pageSubtitle} title={subcategory.pageTitle} />
        <ProgramListSection programs={programs} title={subcategory.programListTitle} />
        {!!subcategory.complimentarySubcategories && (
          <CourseCollectionSection
            complimentarySubcategories={subcategory.complimentarySubcategories}
            description={subcategory.complimentarySubcategoriesDescription}
            title={subcategory.complimentarySubcategoriesTitle}
          />
        )}
        {subcategory.footerBanner && (
          <FooterBannerSection
            ctaLabel={subcategory.footerBanner.ctaLabel}
            ctaLink={subcategory.footerBanner.ctaLink}
            subTitle={subcategory.footerBanner.subTitle}
            theme={subcategory.footerBanner.theme}
            title={subcategory.footerBanner.title}
          />
        )}
      </PageLayout>
    </>
  );
}

export async function getStaticPaths() {
  const subcategories = await getAllSubcategoryContents();
  return {
    paths: subcategories.map((subcategory) => ({ params: { slug: subcategory.slug } })),
    fallback: 'blocking',
  };
}

export async function getStaticProps({ locale, params }: GetStaticPropsContext) {
  const slugSchema = z.string().safeParse(params?.slug);
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE || !slugSchema.success) {
    return {
      notFound: true,
    };
  }

  const slug = slugSchema.data;

  const [page, subcategory] = await Promise.all([getPage('/course-collection'), getSubcategoryContent(slug)]);

  if (!page || !subcategory) {
    return {
      notFound: true,
    };
  }

  const programs = getCatalogItemsFromServer(subcategory.programs.map((program) => program.key));

  if (subcategory.seo) {
    page.seo = subcategory.seo;
    // Note: remove subcategory seo to reduce json size
    subcategory.seo = null;
  }

  return {
    props: {
      page,
      subcategory,
      programs: programs.map((program) =>
        pick(program, ['programDescriptionOverride', 'slug', 'key', 'image', 'summary', 'title'])
      ),
    },
  };
}
